"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ClassicLoader from "@/components/common/classic-loader";
import { FormCheckbox } from "@/components/form/form-checkbox";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { AuthInfoBox } from "@/features/auth/components/elements/auth-info-box";
import { NewPasswordField } from "@/features/auth/components/elements/new-password-field";
import type { RegistrationInput } from "@/features/auth/hooks/use-sign-up-actions";
import { AUTH_PRIMARY_BUTTON } from "@/features/auth/lib/auth-button-styles";
import { AUTH_FORM_IDS } from "@/features/auth/lib/auth-step-config";
import { isPasswordValid } from "@/features/auth/lib/password-rules";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

const FORM_ID = AUTH_FORM_IDS["create-password"];

const formSchema = z.object({
  password: z.string().superRefine((value, ctx) => {
    if (value.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "auth.createPassword.validators.required",
      });
      return;
    }

    if (!isPasswordValid(value)) {
      ctx.addIssue({
        code: "custom",
        message: "auth.createPassword.validators.notMet",
      });
    }
  }),
  terms: z.boolean().refine((value) => value, {
    message: "auth.createPassword.validators.termsRequired",
  }),
  marketing: z.boolean(),
});

const LINK_CLASS =
  "font-medium underline underline-offset-2 hover:no-underline";

type CreatePasswordStepProps = {
  email: string;
  isSubmitting: boolean;
  /** Translation key of a password error returned by Clerk. */
  serverError: string | null;
  /** Hides the inline submit so the host can render it in a bottom bar. */
  submitInFooter?: boolean;
  onSubmit: (input: RegistrationInput) => void;
  onChangeEmail: () => void;
};

export const CreatePasswordStep = ({
  email,
  isSubmitting,
  serverError,
  submitInFooter = false,
  onSubmit,
  onChangeEmail,
}: CreatePasswordStepProps) => {
  const t = useTranslations("auth");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: "", terms: false, marketing: false },
  });

  const { setError } = form;

  useEffect(() => {
    if (serverError) {
      setError("password", { message: serverError });
    }
  }, [serverError, setError]);

  const termsLabel = t.rich("createPassword.consents.terms", {
    terms: (chunks) => (
      <Link
        className={LINK_CLASS}
        href={{ pathname: "/regulations" }}
        target="_blank"
      >
        {chunks}
      </Link>
    ),
    privacy: (chunks) => (
      <Link
        className={LINK_CLASS}
        href={{ pathname: "/privacy-policy" }}
        target="_blank"
      >
        {chunks}
      </Link>
    ),
  });

  return (
    <div className="flex flex-1 flex-col gap-6">
      <AuthInfoBox
        action={
          <button
            className="text-sm text-text leading-[22px] underline underline-offset-4 hover:no-underline"
            onClick={onChangeEmail}
            type="button"
          >
            {t("password.changeEmail")}
          </button>
        }
        description={email}
        title={t("createPassword.title")}
        truncateDescription
      />

      <Form {...form}>
        <form
          className="flex flex-1 flex-col gap-6"
          id={FORM_ID}
          onSubmit={form.handleSubmit((values) =>
            onSubmit({
              marketing: values.marketing,
              password: values.password,
            }),
          )}
        >
          <NewPasswordField
            fieldName="password"
            form={form}
            label={t("password.label")}
          />

          <hr className={cn("border-border", submitInFooter && "mt-auto")} />

          <div className="flex flex-col gap-3">
            <FormCheckbox
              fieldLabel={termsLabel}
              fieldName="terms"
              form={form}
              layout="authConsent"
            />
            <FormCheckbox
              fieldLabel={t("createPassword.consents.marketing")}
              fieldName="marketing"
              form={form}
              layout="authConsent"
            />
          </div>

          {!submitInFooter && (
            <Button
              className={AUTH_PRIMARY_BUTTON}
              disabled={isSubmitting}
              type="submit"
              data-pw="submit-sign-in-button"
            >
              {t("createPassword.submit")}
              {isSubmitting && <ClassicLoader className="ml-2" />}
            </Button>
          )}
        </form>
      </Form>
    </div>
  );
};
